import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCalendarAlt, FaUserInjured, FaPrescriptionBottle, FaFlask,
  FaCreditCard, FaVideo, FaFileMedical, FaPills,
  FaCalendar, FaChartBar, FaCommentDots, FaCog, FaBell, FaShieldAlt, FaUserMd, FaChevronRight
} from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import DashboardCard from '../components/DashboardCard';
import { useApp, useRolePath } from '../context/AppContext';
import { getDashboardData, getDoctors, getFileUrl } from '../services/api';

const Dashboard = () => {
  const { badges, updateBadges, user } = useApp();
  const navigate = useNavigate();
  const rp       = useRolePath();
  const role = user?.role || 'Doctor';
  const [specialists, setSpecialists] = useState([]);

  useEffect(() => {
    getDashboardData()
      .then(res => {
        const d = res.data?.data || res.data || {};
        updateBadges({
          appointments: d.appointments || 0,
          prescriptions: d.prescriptions || 0,
          labTests: d.labTests || 0,
          billing: d.billing || 0,
          telemedicine: d.telemedicine || 0,
          messages: d.messages || 0,
          notifications: d.notifications || 0,
        });
      })
      .catch(() => {});
  }, [updateBadges]);

  useEffect(() => {
    getDoctors().then(res => setSpecialists((res.data?.data || []).slice(0, 6))).catch(() => setSpecialists([]));
  }, []);

  const dashCards = [
    { icon: FaCalendarAlt,        label: 'Appointments',  badgeKey: 'appointments',  path: rp('/appointments') },
    ...(role !== 'Patient' ? [{ icon: FaUserInjured, label: 'Patients', badgeKey: null, path: rp('/patients') }] : []),
    { icon: FaPrescriptionBottle, label: 'Prescriptions', badgeKey: 'prescriptions', path: rp('/prescriptions') },
    { icon: FaFlask,              label: 'Lab Tests',     badgeKey: 'labTests',      path: rp('/labtests') },
    { icon: FaCreditCard,         label: 'Billing',       badgeKey: 'billing',       path: rp('/billing') },
    { icon: FaVideo,              label: 'Telemedicine',  badgeKey: 'telemedicine',  path: rp('/telemedicine') },
    ...(role !== 'Patient' ? [
      { icon: FaFileMedical, label: 'EMR',      badgeKey: null, path: rp('/emr') },
      { icon: FaPills,       label: 'Pharmacy', badgeKey: null, path: rp('/pharmacy') },
      { icon: FaCalendar,    label: 'Calendar', badgeKey: null, path: rp('/calendar') },
      { icon: FaChartBar,    label: 'Reports',  badgeKey: null, path: rp('/reports') },
    ] : []),
    { icon: FaCommentDots, label: 'Messages',      badgeKey: 'messages',      path: rp('/messages') },
    { icon: FaBell,        label: 'Notifications', badgeKey: 'notifications', path: rp('/notifications') },
    { icon: FaCog,         label: 'Settings',      badgeKey: null,            path: rp('/settings') },
    ...(role === 'Admin' ? [{ icon: FaShieldAlt, label: 'Admin Panel', badgeKey: null, path: rp('/panel') }] : []),
  ];

  const greeting = role === 'Admin' ? `Welcome, ${user?.name || 'Admin'}` : role === 'Doctor' ? `Welcome, Dr. ${user?.name?.split(' ')[0] || 'Doctor'}` : `Welcome, ${user?.name?.split(' ')[0] || 'Patient'}`;

  return (
    <div className="page-wrapper">
      <Header title="Dashboard" />
      <Sidebar />
      <main className="page-content">
        <div className="dashboard-greeting">
          <div className="greeting-left">
            <h2 className="greeting-title">{greeting} 👋</h2>
            <p className="greeting-sub">
              {role === 'Admin' ? 'Full system overview and controls.' : role === 'Doctor' ? "Today's schedule and quick tools at a glance." : 'Your health dashboard and appointments.'}
            </p>
          </div>
          <div className="greeting-date">
            {new Date().toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
        </div>

        <div className="dash-stats-row">
          <div className="dash-stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate(rp('/appointments'))}>
            <div className="dash-stat-num">{badges.appointments || 0}</div>
            <div className="dash-stat-label">{role === 'Patient' ? 'My Appts' : "Today's Appts"}</div>
          </div>
          <div className="dash-stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate(rp('/messages'))}>
            <div className="dash-stat-num">{badges.messages || 0}</div>
            <div className="dash-stat-label">New Messages</div>
          </div>
          <div className="dash-stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate(rp('/notifications'))}>
            <div className="dash-stat-num">{badges.notifications || 0}</div>
            <div className="dash-stat-label">Notifications</div>
          </div>
        </div>

        {specialists.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div className="specialists-section-head" onClick={() => navigate(rp('/specialists'))}>
              <div className="section-title" style={{ margin: 0 }}>Meet Our Specialists</div>
              <FaChevronRight style={{ color: 'var(--text-light)' }} />
            </div>
            <div className="specialists-row">
              {specialists.map(doc => (
                <div key={doc.id} className="specialist-mini-card" onClick={() => navigate(rp('/specialists'))}>
                  <div className="specialist-mini-photo">
                    {doc.profileImage ? <img src={getFileUrl(doc.profileImage)} alt={doc.name} /> : <FaUserMd />}
                  </div>
                  <div className="specialist-mini-name">Dr. {doc.name}</div>
                  <div className="specialist-mini-desig">{doc.degree || doc.specialization || 'Specialist'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="section-title" style={{ marginTop: 20 }}>Quick Access</div>
        <div className="dash-grid">
          {dashCards.map(card => (
            <DashboardCard
              key={card.path}
              icon={card.icon}
              label={card.label}
              badge={card.badgeKey ? (badges[card.badgeKey] || 0) : 0}
              path={card.path}
            />
          ))}
        </div>
      </main>
      <BottomNav />

      <style>{`
        .dashboard-greeting { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; }
        .greeting-title { font-size: 20px; font-weight: 800; color: var(--text-dark); }
        .greeting-sub { font-size: 13px; color: var(--text-light); margin-top: 3px; max-width: 260px; line-height: 1.4; }
        .greeting-date { background: white; padding: 6px 12px; border-radius: 10px; font-size: 12px; font-weight: 600; color: var(--primary); box-shadow: var(--shadow); white-space: nowrap; }
        .dash-stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 4px; }
        .dash-stat-card { background: linear-gradient(135deg, #E8500A 0%, #FF6B2C 100%); border-radius: 14px; padding: 14px 10px; text-align: center; box-shadow: 0 4px 14px rgba(232,80,10,0.25); cursor: pointer; transition: transform 0.18s, box-shadow 0.18s; }
        .dash-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(232,80,10,0.35); }
        .dash-stat-num { font-size: 26px; font-weight: 800; color: white; line-height: 1; }
        .dash-stat-label { font-size: 11px; color: rgba(255,255,255,0.85); margin-top: 4px; font-weight: 600; }
        .dash-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
        .specialists-section-head { display: flex; align-items: center; justify-content: space-between; cursor: pointer; }
        .specialists-row { display: flex; gap: 10px; overflow-x: auto; padding: 10px 2px 4px; scrollbar-width: none; }
        .specialists-row::-webkit-scrollbar { display: none; }
        .specialist-mini-card { flex: 0 0 auto; width: 96px; text-align: center; cursor: pointer; }
        .specialist-mini-photo { width: 64px; height: 64px; border-radius: 50%; margin: 0 auto 6px; background: var(--primary-pale); display: flex; align-items: center; justify-content: center; overflow: hidden; font-size: 26px; color: var(--primary); }
        .specialist-mini-photo img { width: 100%; height: 100%; object-fit: cover; }
        .specialist-mini-name { font-size: 12px; font-weight: 700; color: var(--text-dark); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .specialist-mini-desig { font-size: 11px; color: var(--text-light); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        @media (max-width: 400px) { .dash-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; } .greeting-title { font-size: 17px; } }
        @media (min-width: 768px) {
          .dash-grid { grid-template-columns: repeat(5, 1fr); gap: 14px; }
          .dash-stats-row { grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 12px; }
          .dash-stat-card { padding: 20px 16px; border-radius: 18px; }
          .dash-stat-num { font-size: 32px; }
          .dash-stat-label { font-size: 12px; }
          .greeting-title { font-size: 24px; }
          .greeting-sub { font-size: 14px; max-width: 360px; }
        }
        @media (min-width: 1200px) {
          .dash-grid { grid-template-columns: repeat(6, 1fr); gap: 16px; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
